import type { ClientChatMessage } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetComment } from '@/features/api/hooks/grapevine-comment/useGetComment';
import { DeletedRefChatMessage } from '@/features/chats/components/DeletedRefChatMessage';
import { LoadingRefChatMessage } from '@/features/chats/components/LoadingRefChatMessage';
import { RefChatMessage } from '@/features/chats/components/RefChatMessage';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { AppRoute } from '@/navigation/types/navigation';
import { dayjs } from '@/time';

type Props = {
  message: ClientChatMessage;
};

export const CommentRefChatMessage: FC<Props> = ({ message }) => {
  const { t } = useTranslation(LocalizationNameSpace.CHATS, {
    keyPrefix: 'screens.chat.message'
  });

  const deleted = message.referencedEntityDeleted === true;

  // If the comment has not been deleted, the entire comment must always be fetched
  // because the post ID is not included in the referenced entity properties and is required for the link.
  const requiresFetch = !deleted;

  const { data: comment, isFetching } = useGetComment(
    requiresFetch ? message.referencedEntityId : undefined
  );

  const postId = comment?.postId;
  const created = message.referencedEntityCreated ?? comment?.created;
  const date = useMemo(() => dayjs(created).format('L'), [created]);
  const text = message.referencedEntityText ?? comment?.text;

  if (isFetching) {
    return <LoadingRefChatMessage />;
  }

  if (deleted || (requiresFetch && !comment)) {
    return <DeletedRefChatMessage />;
  }

  // Todo: should we scroll to the comment
  return (
    <RefChatMessage
      screen={AppRoute.PostDetail}
      params={{
        id: postId
      }}
      header={t('relatedToComment', { date })}
      content={text}
    />
  );
};
